/**
 * The bodies in the scene: one or more copies of the atlas, each with its own
 * layer state and its own transform.
 *
 * One `BatchedAtlas` per copy, not one shared set of batches holding every
 * copy's instances. That looks wasteful and is deliberate: the runtime merges
 * adjacent slots into a single sub-draw only while they share a matrix and a
 * colour, and giving each copy its own instance matrices inside one batch would
 * mark every instance posed and collapse the merge back to one sub-draw per
 * part. Session 3 measured that difference as 11.11 ms against 16.22 ms
 * (docs/02-QUEST_PERFORMANCE.md), so each copy instead keeps identity matrices
 * and is moved by its own root. The cost is a second copy of the geometry per
 * body, which at the `far` level is about 5 MB.
 *
 * A future refinement is to let the runtime merge instances that share a
 * declared group rather than only unposed ones; that would let every copy live
 * in one batch and share geometry. It is not needed for three bodies.
 */
import { Color, MeshStandardMaterial, Object3D, type Material } from "three";
import { buildBatchedAtlas, loadLevelChunks, type BatchedAtlas, type Manifest, type LoaderPorts, type SystemRecord } from "@atlas/runtime";
import type { Catalog } from "../model.js";
import { resolveVisibility, visibilityDiff, type VisibilityState } from "../rules/visibility.js";
import type { BodyTransform } from "../rules/two-hand-transform.js";

export interface AtlasBody {
  id: string;
  /** Parent of every batch in this copy; moving it moves the whole body. */
  readonly root: Object3D;
  readonly atlas: BatchedAtlas;
  visibility: VisibilityState;
  transform: BodyTransform;
  /** Last resolved mask, kept so the next apply only touches what changed. */
  mask: Uint8Array | null;
  /** The other half of the double buffer, so a resolve allocates nothing. */
  spare: Uint8Array | null;
}

export interface AtlasSceneOptions {
  /** Level to build bodies at; every body in a scene shares one level. */
  level: string;
  /** Systems to build into the batches. Wider than what is visible, so toggling a system on needs no rebuild. */
  systems?: string[];
}

function materialFor(system: SystemRecord): Material {
  const material = new MeshStandardMaterial({ color: new Color(system.colour), roughness: 0.85, metalness: 0 });
  if (system.material === "translucent") {
    // The skin. Opaque enough to read as a body with its skin on (the "Full
    // body" preset and the entry page's first figure), translucent enough to
    // hint at what is under it; the source viewer's 35 % ghost is an X-ray
    // setting for later, not the way a whole body should look.
    material.transparent = true;
    material.opacity = 0.8;
    material.depthWrite = false;
  }
  return material;
}

/**
 * Holds the loaded chunks and the bodies built from them. Chunks are loaded
 * once per level and shared by every body, so a second copy costs a build but
 * not a download.
 */
export class AtlasScene {
  readonly root = new Object3D();
  readonly bodies = new Map<string, AtlasBody>();
  private chunks: Map<number, ArrayBuffer> | null = null;
  private readonly diffBuffer: number[] = [];

  constructor(
    readonly manifest: Manifest,
    readonly catalog: Catalog,
    private readonly ports: LoaderPorts,
    private readonly base: string,
    private readonly options: AtlasSceneOptions,
  ) {
    this.root.name = `atlas:${manifest.id}`;
  }

  /** Download and decode this level's chunks. Safe to call repeatedly. */
  async load(onProgress?: (done: number, total: number) => void): Promise<void> {
    if (this.chunks) return;
    this.chunks = await loadLevelChunks(this.base, this.manifest, this.options.level, this.ports, onProgress);
  }

  /** Add a body, or return the one already under this id. */
  add(id: string, visibility: VisibilityState, transform: BodyTransform): AtlasBody {
    const existing = this.bodies.get(id);
    if (existing) return existing;
    if (!this.chunks) throw new Error("AtlasScene.add before load()");

    const atlas = buildBatchedAtlas(this.manifest, this.chunks, {
      level: this.options.level,
      systems: this.options.systems,
      stable: true,
      materialFor,
    });
    atlas.root.name = `body:${id}`;
    const body: AtlasBody = { id, root: atlas.root, atlas, visibility, transform, mask: null, spare: null };
    this.root.add(atlas.root);
    this.bodies.set(id, body);
    this.applyTransform(body);
    this.applyVisibility(body);
    // Raycast acceleration, one system per idle slice: hit tests through the
    // body cost 3 to 4.5 ms each without the trees (desktop; several times
    // that on a headset) and the interaction runtime asks for one per input
    // source per frame.
    atlas.buildBoundsTreesIdle();
    return body;
  }

  remove(id: string): void {
    const body = this.bodies.get(id);
    if (!body) return;
    this.root.remove(body.root);
    body.atlas.disposeBoundsTrees();
    for (const mesh of body.atlas.meshes.values()) {
      mesh.geometry.dispose();
      const material = mesh.material as Material | Material[];
      if (Array.isArray(material)) for (const m of material) m.dispose();
      else material.dispose();
    }
    this.bodies.delete(id);
  }

  /** Drop every body but keep the loaded chunks, so a relayout is cheap. */
  clear(): void {
    for (const id of [...this.bodies.keys()]) this.remove(id);
  }

  setVisibility(id: string, visibility: VisibilityState): void {
    const body = this.bodies.get(id);
    if (!body) return;
    body.visibility = visibility;
    this.applyVisibility(body);
  }

  setTransform(id: string, transform: BodyTransform): void {
    const body = this.bodies.get(id);
    if (!body) return;
    body.transform = transform;
    this.applyTransform(body);
  }

  /**
   * Resolve the body's layer state and push only what changed into the batches.
   * Every `setVisible` marks a batch dirty, so pushing the whole mask each time
   * would rescan 2,229 slots for a one-part change.
   */
  private applyVisibility(body: AtlasBody): void {
    // Resolve into the spare half and swap: the two masks alternate, so after
    // the first apply nothing here allocates.
    const next = resolveVisibility(this.catalog, body.visibility, body.spare ?? undefined);
    const changed = visibilityDiff(body.mask, next, this.diffBuffer);
    for (const slot of changed) {
      const part = this.catalog.parts[slot];
      if (part) body.atlas.setVisible(part.id, next[slot] === 1);
    }
    body.spare = body.mask;
    body.mask = next;
  }

  private applyTransform(body: AtlasBody): void {
    const { position, scale, yaw } = body.transform;
    body.root.position.set(position.x, position.y, position.z);
    body.root.rotation.set(0, yaw, 0);
    body.root.scale.setScalar(scale);
    body.root.updateMatrixWorld(true);
  }

  /** Raycast trees built, out of the systems that can have one, across every body. */
  boundsTreeStatus(): { built: number; total: number } {
    let built = 0;
    let total = 0;
    for (const body of this.bodies.values()) {
      const s = body.atlas.boundsTreeStatus();
      built += s.built;
      total += s.total;
    }
    return { built, total };
  }

  /** Draw-side totals across every body, for the status readout. */
  stats(): { bodies: number; instances: number; triangles: number; runs: number; culled: number } {
    let instances = 0;
    let triangles = 0;
    let runs = 0;
    let culled = 0;
    for (const body of this.bodies.values()) {
      const s = body.atlas.stats();
      instances += s.instances;
      triangles += s.triangles;
      runs += s.runs;
      culled += s.culled;
    }
    return { bodies: this.bodies.size, instances, triangles, runs, culled };
  }
}
